// Angular
import { Injectable } from '@angular/core';

// Ngrx
import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';

// Rxjs
import { Observable } from 'rxjs';

// Actions
import * as userActions from './user.actions';

// Services
import { SoledadUserService } from '../../shared/services/b2b/user.service';
import { SoledadSearchSettingsService } from '../../shared/services/b2b/searchSettings.service';
import { SoledadCheckoutService } from '../../shared/services/b2b/checkout.service';
import { AppState } from '..';
import { SoledadAuthenticationService } from '../../shared/services/b2b/auth.service';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';

@Injectable()
export class UserEffects {
    constructor(
        private actions$: Actions,
        private userService: SoledadUserService,
        private searchSettingsService: SoledadSearchSettingsService,
        private checkoutService: SoledadCheckoutService,
        private store: Store<AppState>,
        private authService: SoledadAuthenticationService
    ) {}

    getInfo$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(userActions.UserActionsTypes.DO_GET_INFO),
            switchMap((action: userActions.DoGetInfo) =>
                this.userService.getData().pipe(
                    map((data) => new userActions.DoGetInfoOK(data)),
                    catchError((error) => of(new userActions.DoGetInfoKO(error)))
                )
            )
        )
    );

    getInfoAddresses$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(userActions.UserActionsTypes.DO_GET_INFO_OK),
            switchMap((action: userActions.DoGetInfoOK) =>
                this.userService.getUserAddresses(action.payload).pipe(
                    map((data) => new userActions.DoGetAddressesOK(data)),
                    catchError((error) => of(new userActions.DoGetAddressesKO(error)))
                )
            )
        )
    );

    checkRedirection$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(userActions.UserActionsTypes.DO_GET_INFO_OK),
            withLatestFrom(this.store.select((state) => state.user.data)),
            switchMap(([action, user]) => {
                // Check redirection
                this.authService.checkRedirection(user);

                return of({ type: 'NO_ACTION' });
            })
        )
    );

    getSettings$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(userActions.UserActionsTypes.DO_GET_SETTINGS),
            switchMap((action: userActions.DoGetSettings) =>
                this.searchSettingsService.loadConfiguration(action.payload).pipe(
                    map((data) => new userActions.DoGetSettingsOK(data)),
                    catchError((error) => of(new userActions.DoGetSettingsKO(error)))
                )
            )
        )
    );

    getOrderBy$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(userActions.UserActionsTypes.DO_GET_ORDERBY),
            switchMap((action: userActions.DoGetOrderBy) =>
                this.searchSettingsService.loadSearchOrderByOptions(action.payload).pipe(
                    map((data) => new userActions.DoGetOrderByOK(data)),
                    catchError((error) => of(new userActions.DoGetOrderByKO(error)))
                )
            )
        )
    );

    getFrequentSearchTerms$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(userActions.UserActionsTypes.DO_GET_FREQUENT_SEARCH_TERMS),
            switchMap((action: userActions.DoGetFrequentSearchTerms) =>
                this.searchSettingsService.loadUserFrequentSearchTerm(action.payload).pipe(
                    map((data) => new userActions.DoGetFrequentSearchTermsOK(data)),
                    catchError((error) => of(new userActions.DoGetFrequentSearchTermsKO(error)))
                )
            )
        )
    );

    getPayMethods$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(userActions.UserActionsTypes.DO_GET_PAYMETHODS),
            switchMap((action: userActions.DoGetPayMethods) =>
                this.checkoutService.getPaymentOptions(action.payload).pipe(
                    map((data) => new userActions.DoGetPayMethodsOK(data)),
                    catchError((error) => of(new userActions.DoGetPayMethodsKO(error)))
                )
            )
        )
    );

    getAddresses$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(userActions.UserActionsTypes.DO_GET_ADDRESSES),
            switchMap((action: userActions.DoGetAddresses) =>
                this.userService.getUserAddresses(action.payload).pipe(
                    map((data) => new userActions.DoGetAddressesOK(data)),
                    catchError((error) => of(new userActions.DoGetAddressesKO(error)))
                )
            )
        )
    );
}
